import { useState } from 'react';
import { toast } from 'sonner';
import { Layers, Plus, Archive, Pencil, RotateCcw, Check, X } from 'lucide-react';
import { ApiError } from '../../lib/api';
import {
  useAllRoleCategories,
  useCreateRoleCategory,
  useUpdateRoleCategory,
  useArchiveRoleCategory,
  useRestoreRoleCategory,
} from '../../hooks/useRoleCategories';
import { CollapsibleSection } from '../../components/ui/CollapsibleSection';
import { Button } from '../../components/ui/Button';
import { ConfirmModal } from '../../components/ui/ConfirmModal';

const inputClass =
  'block w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm';

/**
 * Manage the role-categories master list, inline on the Job roles page.
 * Categories group the ~100 roles into a handful of buckets (Engineer,
 * Office, Manager) so kit and onboarding templates can target the bucket
 * once instead of every role.
 */
export function RoleCategoriesCard() {
  const list = useAllRoleCategories();
  const create = useCreateRoleCategory();
  const update = useUpdateRoleCategory();
  const archive = useArchiveRoleCategory();
  const restore = useRestoreRoleCategory();

  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [confirm, setConfirm] = useState<{ id: string; name: string } | null>(null);

  const categories = list.data ?? [];
  const active = categories.filter((c) => !c.archived_at);
  const archived = categories.filter((c) => c.archived_at);

  async function onCreate() {
    if (!newName.trim()) return;
    try {
      await create.mutateAsync({ name: newName.trim() });
      toast.success('Category created');
      setNewName('');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create category');
    }
  }

  async function onRename() {
    if (!editingId || !editName.trim()) return;
    try {
      await update.mutateAsync({ id: editingId, name: editName.trim() });
      toast.success('Category renamed');
      setEditingId(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not rename category');
    }
  }

  return (
    <CollapsibleSection
      icon={<Layers className="h-4 w-4 text-fg-muted" />}
      title="Role categories"
      trailing={
        active.length > 0 ? (
          <span className="text-xs text-fg-dim">{active.length}</span>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-fg-muted">
          The handful of buckets roles fall under (e.g. Engineer, Office, Manager).
          Kit and onboarding templates can target a category so the shared baseline
          is defined once; a role&apos;s own template overrides it where they clash.
        </p>

        <div className="flex gap-2">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onCreate()}
            placeholder="e.g. Engineer"
            className={inputClass}
          />
          <Button
            variant="primary"
            size="sm"
            iconLeft={<Plus className="h-4 w-4" />}
            onClick={onCreate}
            loading={create.isPending}
            disabled={!newName.trim()}
          >
            Add
          </Button>
        </div>

        {active.length === 0 ? (
          <p className="text-sm text-fg-dim">No categories yet.</p>
        ) : (
          <ul className="divide-y divide-divider/10">
            {active.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3 py-2">
                {editingId === c.id ? (
                  <>
                    <input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && onRename()}
                      className={inputClass}
                      autoFocus
                    />
                    <div className="flex shrink-0 gap-1">
                      <Button variant="secondary" size="sm" iconLeft={<Check className="h-4 w-4" />} onClick={onRename} loading={update.isPending}>
                        Save
                      </Button>
                      <Button variant="ghost" size="sm" iconLeft={<X className="h-4 w-4" />} onClick={() => setEditingId(null)}>
                        Cancel
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="min-w-0 truncate text-sm text-fg">{c.name}</span>
                    <div className="flex shrink-0 gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconLeft={<Pencil className="h-4 w-4" />}
                        onClick={() => {
                          setEditingId(c.id);
                          setEditName(c.name);
                        }}
                      >
                        Rename
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconLeft={<Archive className="h-4 w-4" />}
                        onClick={() => setConfirm({ id: c.id, name: c.name })}
                      >
                        Archive
                      </Button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        {archived.length > 0 ? (
          <div className="space-y-1">
            <span className="text-xs font-medium text-fg-dim">Archived</span>
            <ul className="divide-y divide-divider/10">
              {archived.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-3 py-2">
                  <span className="min-w-0 truncate text-sm text-fg-muted line-through">{c.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeft={<RotateCcw className="h-4 w-4" />}
                    onClick={async () => {
                      try {
                        await restore.mutateAsync(c.id);
                        toast.success('Category restored');
                      } catch (err) {
                        toast.error(err instanceof ApiError ? err.message : 'Could not restore');
                      }
                    }}
                  >
                    Restore
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <ConfirmModal
        open={!!confirm}
        title="Archive category?"
        message={
          confirm
            ? `"${confirm.name}" will be hidden from the pickers. Roles already in it keep it; you can restore it later.`
            : ''
        }
        tone="warning"
        confirmLabel="Archive"
        onConfirm={async () => {
          if (!confirm) return;
          try {
            await archive.mutateAsync(confirm.id);
            toast.success('Category archived');
          } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not archive');
          }
          setConfirm(null);
        }}
        onClose={() => setConfirm(null)}
      />
    </CollapsibleSection>
  );
}
